import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {useAuth} from "../../context/AuthContext";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import {Input} from "../ui/input";
import {Checkbox} from "../ui/checkbox";
import {Button} from "../ui/button";
import {Separator} from "../ui/separator";
import {useToast} from "../../hooks/use-toast";
import {googleAuth} from "../../api/api";

interface LoginFormProps {
    onSuccess?: () => void;
}

const loginSchema = z.object({
    email: z.string().email({message: "Invalid email address"}),
    password: z.string().min(1, {message: "Password is required"}),
    rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const LoginForm = ({onSuccess}: LoginFormProps) => {
    const {loginUser} = useAuth();
    const {toast} = useToast();
    const form = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: "",
            rememberMe: false,
        },
    });

    const onSubmit = async (data: LoginFormValues) => {
        try {
            await loginUser({
                email: data.email,
                password: data.password,
                remember_me: data.rememberMe,
            });
            onSuccess?.();
        } catch (error) {
            console.error("Login error:", error);
            // toast already handled in AuthContext
        }
    };

    const handleGoogleLogin = () => {
        const g = (window as any).google;
        if (!g?.accounts?.oauth2) {
            toast({
                title: "Google auth failed",
                description: "Google SDK not loaded",
                variant: "destructive",
            });
            return;
        }

        const client = g.accounts.oauth2.initCodeClient({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID as string,
            scope: "openid email profile",
            ux_mode: "popup",
            callback: async (resp: any) => {
                if (resp.code) {
                    try {
                        const res = await googleAuth(resp.code);
                        toast({
                            title: "Success!",
                            description: "Logged in with Google.",
                        });
                        onSuccess?.();
                        window.location.reload();
                    } catch (err: any) {
                        const errMsg =
                            err.response?.data?.error ||
                            err.message ||
                            "Google auth failed";
                        toast({
                            title: "Google auth failed",
                            description: errMsg,
                            variant: "destructive",
                        });
                    }
                }
            },
        });
        client.requestCode();
    };

    const handleFacebookLogin = () => {
        toast({
            title: "Facebook login",
            description: "This feature is coming soon!",
        });
    };

    return (
        <>
            <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
                Welcome Back
            </h2>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="email"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="your@email.com"
                                        type="email"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="password"
                        render={({field}) => (
                            <FormItem>
                                <div className="flex items-center justify-between">
                                    <FormLabel>Password</FormLabel>
                                    <a
                                        href="#"
                                        className="text-xs text-primary hover:underline dark:text-primary-dark"
                                    >
                                        Forgot Password?
                                    </a>
                                </div>
                                <FormControl>
                                    <Input placeholder="••••••••" type="password" {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="rememberMe"
                        render={({field}) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <FormLabel className="text-sm font-normal">
                                    Remember me
                                </FormLabel>
                            </FormItem>
                        )}
                    />

                    <Button
                        type="submit"
                        className="w-full bg-primary font-medium text-white hover:bg-red-700"
                        disabled={form.formState.isSubmitting}
                    >
                        {form.formState.isSubmitting ? "Logging in..." : "Login"}
                    </Button>
                </form>
            </Form>

            <div className="mt-6">
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full"/>
                    </div>
                    <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              Or continue with
            </span>
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                    <Button
                        variant="outline"
                        onClick={handleGoogleLogin}
                        className="flex items-center justify-center"
                    >
                        <svg
                            className="mr-2 h-4 w-4 text-red-500"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 488 512"
                            fill="currentColor"
                        >
                            <path
                                d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/>
                        </svg>
                        <span>Google</span>
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleFacebookLogin}
                        className="flex items-center justify-center"
                    >
                        <svg
                            className="mr-2 h-4 w-4 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            fill="currentColor"
                        >
                            <path
                                d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
                        </svg>
                        <span>Facebook</span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default LoginForm;
